import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Printer, MapPin, Lock, Building2, CheckCircle2, History, Calendar, Package, Truck, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const getBaseURL = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
};

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  processing: { label: "Processing", color: "bg-blue-100 text-blue-800", icon: Package },
  shipped: { label: "Shipped", color: "bg-purple-100 text-purple-800", icon: Truck },
  delivered: { label: "Delivered", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-800", icon: AlertTriangle }
};

export function TonerManagement() {
  const [customerName, setCustomerName] = useState("");
  const [pin, setPin] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [validatedCustomerName, setValidatedCustomerName] = useState("");
  const [printers, setPrinters] = useState([]);
  const [orderValues, setOrderValues] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [orderHistory, setOrderHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showCompatWarning, setShowCompatWarning] = useState(false);
  const [missingTypes, setMissingTypes] = useState({});

  const handleAuthenticate = async () => {
    if (!customerName.trim()) {
      toast.error("Please enter your customer name");
      return;
    }
    if (!pin.trim()) {
      toast.error("Please enter your PIN");
      return;
    }

    setIsAuthenticating(true);

    try {
      const formData = new FormData();
      formData.append("business_name", customerName);
      formData.append("pin", pin);

      const response = await axios.post(
        `${getBaseURL()}/api/toner/validate-business`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.valid) {
        setIsAuthenticated(true);
        setCustomerId(response.data.customer_id);
        setValidatedCustomerName(response.data.customer_name);
        toast.success("Authentication successful");
        
        // Load printers for this customer
        await loadPrinters(response.data.customer_id);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Authentication error:", error);
      toast.error("Failed to authenticate. Please try again.");
    } finally {
      setIsAuthenticating(false);
    }
  };

  const loadPrinters = async (custId) => {
    try {
      const response = await axios.get(
        `${getBaseURL()}/api/toner/printers/${custId}`
      );

      setPrinters(response.data.printers);
      
      // Initialize order values
      const initialOrders = {};
      response.data.printers.forEach(printer => {
        if (printer.asset_type === "color_printer") {
          initialOrders[printer.id] = {
            black: "",
            yellow: "",
            cyan: "",
            magenta: "",
            drum: "",
            black_type: "",
            yellow_type: "",
            cyan_type: "",
            magenta_type: "",
            drum_type: ""
          };
        } else {
          initialOrders[printer.id] = {
            toners: "",
            drum: "",
            toner_type: "",
            drum_type: ""
          };
        }
      });
      setOrderValues(initialOrders);

      if (response.data.printers.length === 0) {
        toast.info("No printers found for your account");
      }
    } catch (error) {
      console.error("Load printers error:", error);
      toast.error("Failed to load printers");
    }
  };

  const loadOrderHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await axios.get(
        `${getBaseURL()}/api/toner/customer-orders/${customerId}`
      );
      setOrderHistory(response.data || []);
      setShowHistoryDialog(true);
    } catch (error) {
      console.error("Load history error:", error);
      toast.error("Failed to load order history");
    } finally {
      setLoadingHistory(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-AU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getOrderItemsSummary = (printers) => {
    const items = [];
    const compat = (t) => t === "compatible" ? " (Compatible)" : "";
    printers.forEach(p => {
      if (p.asset_type === "color_printer") {
        const colors = [];
        if (p.order_black > 0) colors.push(`Black: ${p.order_black}${compat(p.black_type)}`);
        if (p.order_yellow > 0) colors.push(`Yellow: ${p.order_yellow}${compat(p.yellow_type)}`);
        if (p.order_cyan > 0) colors.push(`Cyan: ${p.order_cyan}${compat(p.cyan_type)}`);
        if (p.order_magenta > 0) colors.push(`Magenta: ${p.order_magenta}${compat(p.magenta_type)}`);
        if (colors.length > 0) items.push(`${p.printer_name}: ${colors.join(', ')}`);
      } else {
        if (p.toners_required > 0) items.push(`${p.printer_name}: ${p.toners_required} toner(s)${compat(p.toner_type)}`);
      }
      if (p.drum_required > 0) items.push(`${p.printer_name}: ${p.drum_required} drum(s)${compat(p.drum_type)}`);
    });
    return items;
  };

  const handleOrderChange = (printerId, field, value) => {
    // Only allow numeric values
    if (value === "" || /^\d+$/.test(value)) {
      setOrderValues(prev => ({
        ...prev,
        [printerId]: {
          ...prev[printerId],
          [field]: value
        }
      }));
    }
  };

  const handleTypeChange = (printerId, field, value) => {
    setOrderValues(prev => ({
      ...prev,
      [printerId]: {
        ...prev[printerId],
        [field]: value
      }
    }));
    setMissingTypes(prev => {
      const next = { ...prev };
      delete next[`${printerId}:${field}`];
      return next;
    });
    if (value === "compatible") {
      setShowCompatWarning(true);
    }
  };

  const renderTypeSelect = (printerId, field) => {
    const isMissing = missingTypes[`${printerId}:${field}`];
    return (
      <div>
        <Select
          value={orderValues[printerId]?.[field] || ""}
          onValueChange={(v) => handleTypeChange(printerId, field, v)}
        >
          <SelectTrigger
            className={`h-8 text-xs ${isMissing ? "border-red-500 ring-1 ring-red-400" : ""}`}
            data-testid={`${field.replace(/_/g, "-")}-${printerId}`}
          >
            <SelectValue placeholder="Genuine or Compatible?" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="genuine">Genuine</SelectItem>
            <SelectItem value="compatible">Compatible</SelectItem>
          </SelectContent>
        </Select>
        {isMissing && <p className="text-[11px] text-red-600 mt-1" data-testid={`type-missing-${field.replace(/_/g, "-")}-${printerId}`}>Please choose a type</p>}
      </div>
    );
  };

  // quantity field -> matching type field, per printer kind
  const typeFieldFor = { black: "black_type", yellow: "yellow_type", cyan: "cyan_type", magenta: "magenta_type", toners: "toner_type", drum: "drum_type" };

  const handleSubmit = async () => {
    // Check if any orders were made
    let hasOrders = false;
    printers.forEach(printer => {
      const orders = orderValues[printer.id];
      if (printer.asset_type === "color_printer") {
        if (orders.black || orders.yellow || orders.cyan || orders.magenta || orders.drum) {
          hasOrders = true;
        }
      } else {
        if (orders.toners || orders.drum) {
          hasOrders = true;
        }
      }
    });

    if (!hasOrders) {
      toast.error("Please enter at least one order quantity");
      return;
    }

    // Every ordered item must have an explicit Genuine/Compatible choice
    const missing = {};
    printers.forEach(printer => {
      const orders = orderValues[printer.id] || {};
      const qtyFields = printer.asset_type === "color_printer"
        ? ["black", "yellow", "cyan", "magenta", "drum"]
        : ["toners", "drum"];
      qtyFields.forEach(qf => {
        if (parseInt(orders[qf] || "0") > 0 && !orders[typeFieldFor[qf]]) {
          missing[`${printer.id}:${typeFieldFor[qf]}`] = true;
        }
      });
    });
    if (Object.keys(missing).length > 0) {
      setMissingTypes(missing);
      toast.error("Please choose Genuine or Compatible for each item you are ordering");
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare submission data
      const submissionData = {
        customer_id: customerId,
        customer_name: validatedCustomerName,
        filled_by: validatedCustomerName,
        printers: printers.map(printer => {
          const orders = orderValues[printer.id];
          if (printer.asset_type === "color_printer") {
            return {
              printer_id: printer.id,
              printer_name: printer.name,
              location: printer.location,
              asset_type: "color_printer",
              order_black: parseInt(orders.black || "0"),
              order_yellow: parseInt(orders.yellow || "0"),
              order_cyan: parseInt(orders.cyan || "0"),
              order_magenta: parseInt(orders.magenta || "0"),
              drum_required: parseInt(orders.drum || "0"),
              black_type: orders.black_type || "genuine",
              yellow_type: orders.yellow_type || "genuine",
              cyan_type: orders.cyan_type || "genuine",
              magenta_type: orders.magenta_type || "genuine",
              drum_type: orders.drum_type || "genuine"
            };
          } else {
            return {
              printer_id: printer.id,
              printer_name: printer.name,
              location: printer.location,
              asset_type: "printer",
              toners_required: parseInt(orders.toners || "0"),
              drum_required: parseInt(orders.drum || "0"),
              toner_type: orders.toner_type || "genuine",
              drum_type: orders.drum_type || "genuine"
            };
          }
        })
      };

      await axios.post(
        `${getBaseURL()}/api/toner/submit`,
        submissionData
      );

      setIsSubmitted(true);
      toast.success("Order submitted successfully!");
      
    } catch (error) {
      console.error("Submit error:", error);
      toast.error("Failed to submit order. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewOrder = () => {
    setCustomerName("");
    setPin("");
    setIsAuthenticated(false);
    setCustomerId("");
    setValidatedCustomerName("");
    setPrinters([]);
    setOrderValues({});
    setMissingTypes({});
    setIsSubmitted(false);
  };

  // Success screen after submission
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-4 py-6">
            <div className="flex flex-col items-center text-center">
              <div className="flex flex-col items-center gap-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_onsite-crm-1/artifacts/5ogpwrem_med%20it%20xero%20logo.png"
                  alt="Medical IT"
                  className="h-16 w-auto object-contain"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_onsite-crm-1/artifacts/h18ui4w3_logo%20image.png"
                  alt="Onsite Technology Solutions"
                  className="h-16 w-auto object-contain"
                />
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-16">
          <Card className="text-center">
            <CardContent className="pt-12 pb-12">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Submitted Successfully!</h2>
              <p className="text-gray-600 mb-8">
                Thank you for your toner order. We will process it shortly and contact you if needed.
              </p>
              <Button onClick={handleNewOrder} size="lg">
                Submit Another Order
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex flex-col items-center text-center">
            <div className="flex flex-col items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_onsite-crm-1/artifacts/5ogpwrem_med%20it%20xero%20logo.png"
                alt="Medical IT"
                className="h-16 w-auto object-contain"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_onsite-crm-1/artifacts/h18ui4w3_logo%20image.png"
                alt="Onsite Technology Solutions"
                className="h-16 w-auto object-contain"
              />
            </div>
            <div className="mt-4">
              <h1 className="text-2xl font-bold text-gray-900">Toner & Supplies Ordering</h1>
              <p className="text-gray-600 mt-1">Order toners and drums for your printers</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {!isAuthenticated ? (
          /* Authentication Form */
          <Card className="max-w-md mx-auto">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-blue-600" />
              </div>
              <CardTitle>Customer Login</CardTitle>
              <CardDescription>Enter your customer name and PIN to access your printers</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="customerName">Customer Name</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="customerName"
                    type="text"
                    placeholder="Enter your customer/business name"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="pl-10"
                    data-testid="customer-name-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="pin">PIN</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="pin"
                    type="password"
                    placeholder="Enter your PIN"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    className="pl-10"
                    data-testid="pin-input"
                    onKeyDown={(e) => e.key === 'Enter' && handleAuthenticate()}
                  />
                </div>
              </div>

              <Button 
                onClick={handleAuthenticate} 
                className="w-full" 
                disabled={isAuthenticating}
                data-testid="login-button"
              >
                {isAuthenticating ? "Verifying..." : "Access My Printers"}
              </Button>

              <p className="text-sm text-gray-500 text-center mt-4">
                Don't have a PIN? Contact us at <strong>1300 234 972</strong>
              </p>
            </CardContent>
          </Card>
        ) : (
          /* Printer List and Order Form */
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Welcome, {validatedCustomerName}</CardTitle>
                    <CardDescription>Select quantities for toners and drums you need to order</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={loadOrderHistory}
                      disabled={loadingHistory}
                      data-testid="view-history-button"
                    >
                      <History className="h-4 w-4 mr-2" />
                      {loadingHistory ? "Loading..." : "Order History"}
                    </Button>
                    <Button variant="outline" onClick={handleNewOrder}>
                      Logout
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Delivery Notice */}
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
              <p className="text-red-700 font-bold text-center">
                Please note delivery for orders under $100 will be $10.99. Free Delivery for orders over $100. Please allow 4-6 working days for delivery.
              </p>
            </div>

            {printers.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Printer className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900">No Printers Found</h3>
                  <p className="text-gray-500 mt-2">
                    No printers are registered for your account. Please contact support.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Printer Cards */}
                <div className="space-y-4">
                  {printers.map((printer) => (
                    <Card key={printer.id} className="overflow-hidden">
                      <CardHeader className="bg-gray-50 border-b">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            printer.asset_type === "color_printer" 
                              ? "bg-gradient-to-r from-cyan-500 via-magenta-500 to-yellow-500" 
                              : "bg-blue-100"
                          }`}>
                            <Printer className={`w-5 h-5 ${
                              printer.asset_type === "color_printer" ? "text-white" : "text-blue-600"
                            }`} />
                          </div>
                          <div>
                            <CardTitle className="text-lg">{printer.name}</CardTitle>
                            <div className="flex items-center gap-1 text-sm text-gray-500">
                              <MapPin className="w-3 h-3" />
                              {printer.location}
                            </div>
                          </div>
                          <span className={`ml-auto px-3 py-1 rounded-full text-xs font-medium ${
                            printer.asset_type === "color_printer" 
                              ? "bg-purple-100 text-purple-700" 
                              : "bg-blue-100 text-blue-700"
                          }`}>
                            {printer.asset_type === "color_printer" ? "Color Printer" : "Printer"}
                          </span>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-4">
                        {printer.asset_type === "color_printer" ? (
                          /* Color Printer - 4 toner colors + drum */
                          <div className="space-y-4">
                            <p className="text-sm text-gray-600 font-medium">Order Color Toners:</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div className="space-y-2">
                                <Label className="text-xs flex items-center gap-2">
                                  <span className="w-3 h-3 rounded-full bg-black"></span>
                                  Order Black
                                </Label>
                                <Input
                                  type="text"
                                  placeholder="0"
                                  value={orderValues[printer.id]?.black || ""}
                                  onChange={(e) => handleOrderChange(printer.id, "black", e.target.value)}
                                  className="text-center"
                                  data-testid={`order-black-${printer.id}`}
                                />
                                {renderTypeSelect(printer.id, "black_type")}
                              </div>
                              <div className="space-y-2">
                                <Label className="text-xs flex items-center gap-2">
                                  <span className="w-3 h-3 rounded-full bg-yellow-400"></span>
                                  Order Yellow
                                </Label>
                                <Input
                                  type="text"
                                  placeholder="0"
                                  value={orderValues[printer.id]?.yellow || ""}
                                  onChange={(e) => handleOrderChange(printer.id, "yellow", e.target.value)}
                                  className="text-center"
                                  data-testid={`order-yellow-${printer.id}`}
                                />
                                {renderTypeSelect(printer.id, "yellow_type")}
                              </div>
                              <div className="space-y-2">
                                <Label className="text-xs flex items-center gap-2">
                                  <span className="w-3 h-3 rounded-full bg-cyan-500"></span>
                                  Order Cyan
                                </Label>
                                <Input
                                  type="text"
                                  placeholder="0"
                                  value={orderValues[printer.id]?.cyan || ""}
                                  onChange={(e) => handleOrderChange(printer.id, "cyan", e.target.value)}
                                  className="text-center"
                                  data-testid={`order-cyan-${printer.id}`}
                                />
                                {renderTypeSelect(printer.id, "cyan_type")}
                              </div>
                              <div className="space-y-2">
                                <Label className="text-xs flex items-center gap-2">
                                  <span className="w-3 h-3 rounded-full bg-pink-500"></span>
                                  Order Magenta
                                </Label>
                                <Input
                                  type="text"
                                  placeholder="0"
                                  value={orderValues[printer.id]?.magenta || ""}
                                  onChange={(e) => handleOrderChange(printer.id, "magenta", e.target.value)}
                                  className="text-center"
                                  data-testid={`order-magenta-${printer.id}`}
                                />
                                {renderTypeSelect(printer.id, "magenta_type")}
                              </div>
                            </div>
                            <div className="pt-2 border-t">
                              <div className="w-48 space-y-2">
                                <Label className="text-xs">Drum Required</Label>
                                <Input
                                  type="text"
                                  placeholder="0"
                                  value={orderValues[printer.id]?.drum || ""}
                                  onChange={(e) => handleOrderChange(printer.id, "drum", e.target.value)}
                                  className="text-center"
                                  data-testid={`order-drum-${printer.id}`}
                                />
                                {renderTypeSelect(printer.id, "drum_type")}
                              </div>
                            </div>
                          </div>
                        ) : (
                          /* Regular Printer - Toners + Drum */
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label className="text-sm">Please enter number of toners required</Label>
                                <Input
                                  type="text"
                                  placeholder="0"
                                  value={orderValues[printer.id]?.toners || ""}
                                  onChange={(e) => handleOrderChange(printer.id, "toners", e.target.value)}
                                  className="text-center"
                                  data-testid={`order-toners-${printer.id}`}
                                />
                                {renderTypeSelect(printer.id, "toner_type")}
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm">Drum required</Label>
                                <Input
                                  type="text"
                                  placeholder="0"
                                  value={orderValues[printer.id]?.drum || ""}
                                  onChange={(e) => handleOrderChange(printer.id, "drum", e.target.value)}
                                  className="text-center"
                                  data-testid={`order-drum-${printer.id}`}
                                />
                                {renderTypeSelect(printer.id, "drum_type")}
                              </div>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Submit Button */}
                <Card>
                  <CardContent className="py-6">
                    <Button 
                      onClick={handleSubmit} 
                      className="w-full" 
                      size="lg"
                      disabled={isSubmitting}
                      data-testid="submit-order-button"
                    >
                      {isSubmitting ? "Submitting Order..." : "Submit Order"}
                    </Button>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-sm text-gray-500">
          <p>Need help? Contact us at <strong>1300 234 972</strong></p>
          <p className="mt-1">Onsite Technology Solutions</p>
        </div>
      </footer>

      {/* Compatible Warning Dialog */}
      <Dialog open={showCompatWarning} onOpenChange={setShowCompatWarning}>
        <DialogContent className="max-w-sm" data-testid="compatible-warning-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Please Note
            </DialogTitle>
            <DialogDescription className="text-base text-gray-700 pt-2">
              Using Compatible toner/drum can void your warranty.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setShowCompatWarning(false)} data-testid="compatible-warning-ok">
              I Understand
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Order History
            </DialogTitle>
            <DialogDescription>
              Your past toner orders and their status
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {orderHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No order history found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {orderHistory.map((order) => {
                  const StatusIcon = STATUS_CONFIG[order.status]?.icon || Clock;
                  const deliveredEntry = order.status_history?.find(h => h.status === "delivered");
                  
                  return (
                    <div key={order.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="text-sm font-medium">
                            Ordered: {formatDate(order.submission_date)}
                          </span>
                        </div>
                        <Badge className={STATUS_CONFIG[order.status]?.color || "bg-gray-100"}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {STATUS_CONFIG[order.status]?.label || order.status}
                        </Badge>
                      </div>
                      
                      {order.status === "delivered" && deliveredEntry && (
                        <div className="flex items-center gap-2 mb-3 text-green-600 text-sm">
                          <CheckCircle2 className="h-4 w-4" />
                          <span>Delivered: {formatDate(deliveredEntry.timestamp)}</span>
                        </div>
                      )}
                      
                      {order.tracking_code && (
                        <div className="flex items-center gap-2 mb-3 text-purple-600 text-sm">
                          <Truck className="h-4 w-4" />
                          <span>Tracking: {order.tracking_code}</span>
                        </div>
                      )}
                      
                      <div className="bg-gray-50 rounded p-3">
                        <p className="text-xs text-gray-500 mb-2 font-medium">Items Ordered:</p>
                        <ul className="text-sm space-y-1">
                          {getOrderItemsSummary(order.printers).map((item, idx) => (
                            <li key={idx} className="text-gray-700">• {item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistoryDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TonerManagement;
